import { useMemo } from "react";
import {
  areObjectsEqual,
  focusElement,
  orderStatusList,
  inputOnEnter,
  userData,
  contactLensesOrderStructure,
  contactLensesUseOptions,
  selectedOptic,
} from "../js/utils";
import InputIconSearch from "./InputIconSearch";
import InputData from "./InputData";
import InputDataControl from "../support-extra/components/InputDataControl";
import Dropdown from "./Dropdown";
import { OrderActionButtons } from "./OrderActionButtons";
import CheckBox from "./CheckBox";
import { post } from "../js/post";
import { useModelsTradeNamesAndProducts } from "../hooks/useModelsTradeNamesAndProducts";
import { useCLTypes } from "../hooks/useCLTypes";
import { useOrderBase } from "../hooks/useOrderBase";

export function ContactLensesOrder({
  orderId,
  createPopupQuestion,
  removePopupQuestion,
  onSave,
  onClose,
}) {
  const {
    initialOrder,
    currentOrder,
    setCurrentOrder,
    currentOrderFixed,
    setCurrentOrderFixed,
    selectedSupplier,
    setSelectedSupplier,
    searchCode,
    setSearchCode,
    supplierList,
    brandList,
    validateOrder,
    focusFields,
    saveOrder,
    deleteOrder,
  } = useOrderBase({
    orderId,
    familia: 2,
    structure: contactLensesOrderStructure,
    requiredFields: ["proveedor", "marca", "nombreComercial"],
    fieldsToFocus: [
      { field: "proveedor", elementId: 51 },
      { field: "marca", elementId: 52 },
      { field: "nombreComercial", elementId: 53 },
    ],
    saveEndpoint: "support-extra/pedidos/GuardarPedidoLenteContacto.php",
    getOrderEndpoint: "support-extra/pedidos/ObtenerPedidoLenteContacto.php",
    onSave,
    createPopupQuestion,
    removePopupQuestion,
  });
  const rawTypeList = useCLTypes();
  const typeList = useMemo(
    () => [{ id: "", text: "" }, ...rawTypeList],
    [rawTypeList],
  );
  const [rawModelsList] = useModelsTradeNamesAndProducts(
    [2],
    currentOrder.proveedor,
    currentOrder.marca !== "" ? [currentOrder.marca] : [],
  );
  const modelsList = useMemo(() => {
    return [
      { id: "", text: "" },
      ...rawModelsList.map((e) => ({
        id: e,
        text: e,
      })),
    ];
  }, [rawModelsList]);

  // console.log("currentOrder: ", currentOrder);
  // console.log("selectedSupplier: ", selectedSupplier);
  // console.log("brandList: ", brandList);

  const handleProductChange = (field, value) => {
    setCurrentOrder((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  async function getProduct() {
    const response = await post("ObtenerLenteContacto.php", {
      token: userData.token,
      codigo: searchCode,
      tipoCodigo: -1,
      idOptica: selectedOptic.id,
    });
    // console.log(
      // "BUSCANDO LENTE DE CONTACTO: ",
      // {
        // token: userData.token,
        // codigo: searchCode,
        // tipoCodigo: -1,
        // idOptica: selectedOptic.id,
      // },
      // response,
    // );
    if (response.id) {
      const productFields = {
        codigo: response.codigoOPTIWIN || response.codigoEANUPC,
        proveedor: response.idProveedor,
        marca: response.marca,
        nombreComercial: response.nombreComercial,
        tipo: response.tipo,
        color: response.color,
        desechable: response.desechable,
        unidadesCaja: response.unidades,
        uso: response.uso,
        refProveedor: response.referenciaProveedor,
        esf: response.esf,
        cil: response.cil,
        eje: response.eje,
        add: response.add,
        total: response.total,
        rad1: response.rad1,
        rad2: response.rad2,
        dia: response.dia,
        hid: response.hid,
        zo: response.zo,
        ela: response.ela,
      };
      setCurrentOrder((prev) => ({ ...prev, ...productFields }));
      setCurrentOrderFixed((prev) => ({ ...prev, ...productFields }));
      setSearchCode(productFields.codigo);
      const supplier = supplierList.find(
        (s) => s.id === parseInt(response.idProveedor),
      );
      if (supplier) setSelectedSupplier(supplier);
    } else {
      setCurrentOrder(initialOrder);
      setCurrentOrderFixed(initialOrder);
      setSelectedSupplier({ id: "", text: "" });
      createPopupQuestion(
        "Producto no encontrado.",
        async () => {
          focusElement(50);
          removePopupQuestion();
        },
        null,
        "Entendido",
      );
    }
  }

  return (
    <div className="popup-container">
      <div className="ap-container popup-screen">
        <div className="popup-screen-header">
          <span className="popup-screen-header-title">
            Pedido de lentes de contacto
          </span>
          <button
            className="popup-action-icon close-icon"
            onClick={() => {
              if (!areObjectsEqual(currentOrder, currentOrderFixed)) {
                createPopupQuestion(
                  "Existen cambios sin guardar. ¿Desea salir de la ficha de pedido?",
                  async () => {
                    removePopupQuestion();
                    onClose();
                  },
                  () => {
                    removePopupQuestion();
                  },
                  "Sí",
                  "No",
                );
              } else onClose();
            }}
          >
            <img src="/images/close-white.svg" loading="lazy" alt="Icon"></img>
          </button>
        </div>
        <div
          className="tab-content ap-content"
          style={{ overflow: "unset", backgroundColor: "#f9f9f9" }}
        >
          <div
            className="container-panel"
            style={{ display: "flex", flexDirection: "column", gap: 10 }}
          >
            <div className="opt-table-title">Datos del producto</div>
            <div className="opt-table-content grid-5" id="order-product-grid">
              <div className="cell cell-label cell-start-content">
                Código/EAN/UPC
              </div>
              <div className="cell">
                <InputIconSearch
                  tabIndex={50}
                  type="type-alphanum-ext"
                  size={0}
                  maxLength={10}
                  input={searchCode}
                  onChange={(e) => {
                    setSearchCode(e);
                  }}
                  onClear={() => {
                    focusElement(50);
                    setSearchCode("");
                  }}
                  onEnter={async () => {
                    if (searchCode) await getProduct();
                  }}
                ></InputIconSearch>
              </div>
              <div className="cell combined-cells-3"></div>
              <div className="cell cell-label cell-start-content">
                Proveedor (*)
              </div>
              <div className="cell combined-cells-4">
                <Dropdown
                  tabIndex={51}
                  optionList={supplierList}
                  size={0}
                  mandatory={true}
                  op={selectedSupplier.id}
                  onChange={(e) => {
                    // console.log("PROVEEDOR SELECCIONADO: ", e);
                    setSelectedSupplier(e);
                  }}
                ></Dropdown>
              </div>
              <div className="cell cell-label cell-start-content">
                Marca (*)
              </div>
              <div className="cell combined-cells-4">
                <Dropdown
                  tabIndex={52}
                  optionList={brandList}
                  size={0}
                  mandatory={false}
                  op={currentOrder.marca}
                  onChange={(e) => {
                    setCurrentOrder((prev) => ({
                      ...prev,
                      marca: e.text,
                    }));
                  }}
                ></Dropdown>
              </div>
              <div className="cell cell-label cell-start-content">
                Nombre comercial (*)
              </div>
              <div className="cell combined-cells-4">
                <Dropdown
                  tabIndex={53}
                  optionList={modelsList}
                  size={0}
                  mandatory={false}
                  op={currentOrder.nombreComercial}
                  onChange={(e) => {
                    setCurrentOrder((prev) => ({
                      ...prev,
                      nombreComercial: e.text,
                    }));
                  }}
                ></Dropdown>
              </div>
              <div className="cell cell-label cell-start-content">Tipo</div>
              <div className="cell">
                <Dropdown
                  tabIndex={54}
                  optionList={typeList}
                  size={0}
                  mandatory={false}
                  op={currentOrder.tipo}
                  onChange={(e) => {
                    setCurrentOrder((prev) => ({
                      ...prev,
                      tipo: e.text,
                    }));
                  }}
                ></Dropdown>
              </div>
              <div className="cell"></div>
              <div className="cell cell-label cell-start-content">Color</div>
              <div className="cell">
                <InputData
                  tabIndex={57}
                  type="type-alphanum-ext"
                  size={0}
                  input={currentOrder.color}
                  onChange={(e) => {
                    setCurrentOrder((prev) => ({
                      ...prev,
                      color: e,
                    }));
                  }}
                ></InputData>
              </div>
              <div className="cell cell-label cell-start-content">
                Desechable
              </div>
              <div className="cell cell-start-content">
                <CheckBox
                  tabIndex={55}
                  active={currentOrder.desechable}
                  onClick={() => {
                    setCurrentOrder((prev) => ({
                      ...prev,
                      desechable: !prev.desechable,
                    }));
                  }}
                ></CheckBox>
              </div>
              <div className="cell"></div>
              <div className="cell cell-label cell-start-content">
                Unidades x caja
              </div>
              <div className="cell">
                <InputData
                  tabIndex={58}
                  type="type-integer"
                  size={0}
                  input={currentOrder.unidadesCaja}
                  onChange={(e) => {
                    setCurrentOrder((prev) => ({
                      ...prev,
                      unidadesCaja: e,
                    }));
                  }}
                ></InputData>
              </div>
              <div className="cell cell-label cell-start-content">Uso</div>
              <div className="cell">
                <Dropdown
                  tabIndex={56}
                  optionList={contactLensesUseOptions}
                  size={0}
                  mandatory={false}
                  op={currentOrder.uso}
                  onChange={(e) => {
                    setCurrentOrder((prev) => ({
                      ...prev,
                      uso: e.text,
                    }));
                  }}
                ></Dropdown>
              </div>
              <div className="cell"></div>
              <div className="cell cell-label cell-start-content">
                Ref. proveedor
              </div>
              <div className="cell">
                <InputData
                  tabIndex={60}
                  maxLength={20}
                  type="type-alphanum-ext"
                  size={0}
                  input={currentOrder.refProveedor}
                  onChange={(e) => {
                    setCurrentOrder((prev) => ({
                      ...prev,
                      refProveedor: e,
                    }));
                  }}
                ></InputData>
              </div>
              <div className="cell combined-cells-5">
                <div className="opt-table-content grid-11">
                  <div className="cell cell-label">Esf</div>
                  <div className="cell cell-label">Cil</div>
                  <div className="cell cell-label">Eje</div>
                  <div className="cell cell-label">Add</div>
                  <div className="cell cell-label">Total</div>
                  <div className="cell cell-label">Rad 1</div>
                  <div className="cell cell-label">Rad 2</div>
                  <div className="cell cell-label">Diá</div>
                  <div className="cell cell-label">Hid</div>
                  <div className="cell cell-label">ZO</div>
                  <div className="cell cell-label">Ela</div>
                  <div className="cell">
                    <InputDataControl
                      tabIndex={61}
                      type="type-optofloat"
                      size={0}
                      input={currentOrder.esf}
                      minValue=""
                      maxValue=""
                      step={0.25}
                      numDecimales=""
                      createPopupQuestion={createPopupQuestion}
                      removePopupQuestion={removePopupQuestion}
                      onChange={(e) => {
                        handleProductChange("esf", e);
                      }}
                    ></InputDataControl>
                  </div>
                  <div className="cell">
                    <InputDataControl
                      tabIndex={62}
                      type="type-optofloat"
                      size={0}
                      input={currentOrder.cil}
                      minValue=""
                      maxValue=""
                      step={0.25}
                      numDecimales=""
                      createPopupQuestion={createPopupQuestion}
                      removePopupQuestion={removePopupQuestion}
                      onChange={(e) => {
                        handleProductChange("cil", e);
                      }}
                    ></InputDataControl>
                  </div>
                  <div className="cell">
                    <InputData
                      tabIndex={63}
                      type="type-integer"
                      size={0}
                      input={currentOrder.eje}
                      onChange={(e) => {
                        handleProductChange("eje", e);
                      }}
                    ></InputData>
                  </div>
                  <div className="cell">
                    <InputDataControl
                      tabIndex={64}
                      type="type-optofloat"
                      size={0}
                      input={currentOrder.add}
                      minValue=""
                      maxValue=""
                      step={0.25}
                      numDecimales=""
                      createPopupQuestion={createPopupQuestion}
                      removePopupQuestion={removePopupQuestion}
                      onChange={(e) => {
                        handleProductChange("add", e);
                      }}
                    ></InputDataControl>
                  </div>
                  <div className="cell">
                    <InputData
                      tabIndex={65}
                      type="type-float2"
                      size={0}
                      input={currentOrder.total}
                      onChange={(e) => {
                        handleProductChange("total", e);
                      }}
                    ></InputData>
                  </div>
                  <div className="cell">
                    <InputData
                      tabIndex={66}
                      maxLength={6}
                      type="type-alphanum-ext"
                      size={0}
                      input={currentOrder.rad1}
                      onChange={(e) => {
                        handleProductChange("rad1", e);
                      }}
                    ></InputData>
                  </div>
                  <div className="cell">
                    <InputData
                      tabIndex={67}
                      maxLength={6}
                      type="type-alphanum-ext"
                      size={0}
                      input={currentOrder.rad2}
                      onChange={(e) => {
                        handleProductChange("rad2", e);
                      }}
                    ></InputData>
                  </div>
                  <div className="cell">
                    <InputData
                      tabIndex={68}
                      type="type-float2"
                      size={0}
                      input={currentOrder.dia}
                      onChange={(e) => {
                        handleProductChange("dia", e);
                      }}
                    ></InputData>
                  </div>
                  <div className="cell">
                    <InputData
                      tabIndex={69}
                      type="type-integer"
                      size={0}
                      input={currentOrder.hid}
                      onChange={(e) => {
                        handleProductChange("hid", e);
                      }}
                    ></InputData>
                  </div>
                  <div className="cell">
                    <InputData
                      tabIndex={70}
                      type="type-float2"
                      size={0}
                      input={currentOrder.zo}
                      onChange={(e) => {
                        handleProductChange("zo", e);
                      }}
                    ></InputData>
                  </div>
                  <div className="cell">
                    <InputData
                      tabIndex={71}
                      type="type-float2"
                      size={0}
                      input={currentOrder.ela}
                      onChange={(e) => {
                        handleProductChange("ela", e);
                      }}
                    ></InputData>
                  </div>
                </div>
              </div>
            </div>
            <div className="opt-table-title" style={{ marginTop: 20 }}>
              Datos del pedido:{" "}
              {currentOrder.idPedido === -1
                ? selectedOptic.nombre
                : currentOrder.nombreOptica}
            </div>
            <div className="opt-table-content grid-5" id="order-product-grid">
              <div className="cell cell-label cell-start-content">
                Fecha pedido
              </div>
              <div className="cell">
                <input
                  type="date"
                  max="9999-12-31"
                  tabIndex={72}
                  className="input-field input-cell"
                  value={currentOrder.fechaPedido}
                  onChange={(e) => {
                    setCurrentOrder((prev) => ({
                      ...prev,
                      fechaPedido: e.target.value,
                    }));
                  }}
                  onKeyUp={(e) => inputOnEnter(e)}
                ></input>
              </div>
              <div className="cell"></div>
              <div className="cell cell-label cell-start-content">Estado</div>
              <div className="cell">
                <Dropdown
                  tabIndex={74}
                  optionList={orderStatusList}
                  size={0}
                  op={currentOrder.estado}
                  onChange={(e) => {
                    setCurrentOrder((prev) => ({
                      ...prev,
                      estado: e.id,
                    }));
                  }}
                ></Dropdown>
              </div>
              <div className="cell cell-label cell-start-content">
                Fecha recepción
              </div>
              <div className="cell">
                <input
                  type="date"
                  max="9999-12-31"
                  tabIndex={73}
                  className="input-field input-cell"
                  value={currentOrder.fechaRecepcion}
                  onChange={(e) => {
                    setCurrentOrder((prev) => ({
                      ...prev,
                      fechaRecepcion: e.target.value,
                    }));
                  }}
                  onKeyUp={(e) => inputOnEnter(e)}
                ></input>
              </div>
              <div className="cell"></div>
              <div className="cell cell-label cell-start-content">Unidades</div>
              <div className="cell">
                <button
                  className="incdec-btn"
                  onClick={() => {
                    setCurrentOrder((prev) => ({
                      ...prev,
                      unidades:
                        parseInt(prev.unidades) >= 2
                          ? parseInt(prev.unidades) - 1
                          : parseInt(prev.unidades),
                    }));
                  }}
                >
                  -
                </button>
                <div style={{ flex: 1 }}>
                  <InputData
                    tabIndex={75}
                    type="type-integer"
                    size={0}
                    input={currentOrder.unidades}
                    onChange={(e) => {
                      setCurrentOrder((prev) => ({
                        ...prev,
                        unidades: e,
                      }));
                    }}
                  ></InputData>
                </div>
                <button
                  className="incdec-btn"
                  onClick={() => {
                    setCurrentOrder((prev) => ({
                      ...prev,
                      unidades: parseInt(prev.unidades) + 1,
                    }));
                  }}
                >
                  +
                </button>
              </div>
            </div>
            <OrderActionButtons
              validateOrder={validateOrder}
              focusFields={focusFields}
              saveOrder={saveOrder}
              deleteOrder={deleteOrder}
              onSave={onSave}
              onClose={onClose}
              idPedido={currentOrder.idPedido}
              createPopupQuestion={createPopupQuestion}
              removePopupQuestion={removePopupQuestion}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
